import { useMemo } from "react";
import { atom } from "recoil";
import ROUTE_PATTERNS from "../consts/routes";

// store real time updates that are ready to be consumed by the app
export const realTimeUpdatesState = atom({
    key: 'real-time-updates',
    default: [],
})

export const clickedPathsState = atom({
    key: 'clicked-paths',
    default: [],
})

// map of paths to their query keys
export const pathQueryKeyMap = {
    [ROUTE_PATTERNS.DASHBOARD]: [
        'dashboardQueryKey',
    ],
    [ROUTE_PATTERNS.GAME_EDIT]: [
        'gameUri',
        'zonesQueryKey',
        'waypointsQueryKey',
    ],
    [ROUTE_PATTERNS.TASKS_ORGANIZE]: [
        'challengesQueryKey',
        'nubsQueryKey',
        'judgeQueryKey',
    ],
    [ROUTE_PATTERNS.GAMES_PATH]: [
        'challengesQueryKey',
    ],
    [ROUTE_PATTERNS.TEAMS_ORGANIZE]: [
        'teamsQueryKey',
        'infiniteTeamsOptionsQueryKey',
        'administratorsQueryKey',
        'coordinatorsQueryKey',
        'bannedQueryKey',
    ],
    [ROUTE_PATTERNS.GROUPS_ORGANIZE]: [
        'groupsQueryKey',
    ],
    [ROUTE_PATTERNS.GAMES_REWARDS]: [
        'rewardsQueryKey',
    ],
    [ROUTE_PATTERNS.GAMES_EXPORTS]: [
        'gameUri',
    ],
    [ROUTE_PATTERNS.ANSWERS_MONITOR]: [
        'infiniteAnswerCommentsQueryKey',
        'infiniteTeamsOptionsQueryKey',
        'monitorQueryKey',
        'leaderboardQueryKey',
    ],
    [ROUTE_PATTERNS.GAMES_GREY]: [
        'gameUri',
        'gameBrandingUri',
    ],
    [ROUTE_PATTERNS.GAMES_REELS]: [
        'gameUri',
    ],
    [ROUTE_PATTERNS.TASKS_LIBRARY]: [
        'libraryQueryKey',
    ],
    [ROUTE_PATTERNS.GAMES_STREAM]: [
        'gameStreamQueryKey',
        'leaderboardQueryKey',
    ],
    [ROUTE_PATTERNS.TASKS_STREAM]: [
        'challengeStreamQueryKey',
        'leaderboardQueryKey',
    ],
    [ROUTE_PATTERNS.TEAMS_STREAM]: [
        'teamStreamQueryKey',
        'leaderboardQueryKey',
    ],
    [ROUTE_PATTERNS.TEAMS_LEADERBOARD]: [
        'leaderboardQueryKey',
    ],
    [ROUTE_PATTERNS.ANSWERS_SHOW]: [
        'answerQueryKey',
        'infiniteAnswerCommentsQueryKey',
    ],
    [ROUTE_PATTERNS.GAMES_SLIDESHOW]: [
        'slideshowQueryKey',
    ],
    [ROUTE_PATTERNS.GAMES_PERSONALIZATIONS]: [
        'personalizationsUri',
    ],
    [ROUTE_PATTERNS.GAMES_ANALYTICS]: [
        'analyticsQueryKey',
    ],
    [ROUTE_PATTERNS.GAMES_LIVE]: [],
    [ROUTE_PATTERNS.GAMES_DONATE]: [
        'gameUri',
    ],
    [ROUTE_PATTERNS.GAMES_NPC]: [
        'npcsQueryKey',
    ],
    [ROUTE_PATTERNS.GAMES_DESIGNATIONS]: [
        'designationsQueryKey',
    ]
};

// Routes where real-time updates should be applied immediately rather than queued.
export const instantUpdateRoutes = [
    ROUTE_PATTERNS.GAME_EDIT,
];

// Message sources that should trigger instant refresh (bypass queue)
export const instantUpdateSources = [
    "ss.purchase.donation",
    "ss.purchase.library",
    "ss.game.reel",
    "ss.task.reel",
    "ss.team.reel",
    "ss.game.export",
];

/**
 * Single source of truth for route-to-listener mappings
 * Defines which event sources each route listens to and how to resolve IDs
 * 
 * @type {Object.<string, Object.<string, string|function>>}
 */
const routeListenerMap = {
    [ROUTE_PATTERNS.TEAMS_ORGANIZE]: {
        "ss.game.join": "*",
        "ss.team.delete": "teamIds",
        "ss.team.update": "teamIds",
        "ss.team.create": "*",
        "ss.team.merge": "*",
        "ss.team.photo": "teamIds",
        "ss.team.rename": "teamIds",
        "ss.team.unmerge": "*",
        "ss.admin.create": "*",
        "ss.admin.delete": "administratorIds",
        "ss.coordinator.create": "*",
        "ss.coordinator.delete": "coordinatorIds",
        "ss.user.ban": "*",
        "ss.user.unban": "bannedIds",
    },
    [ROUTE_PATTERNS.GAMES_SLIDESHOW]: {
        "ss.answer.reject": "answerIds",
        "ss.answer.feature": (data) => data.featured ? "*" : null,
        "ss.answer.unfeature": (data) => data.featured ? "*" : null,
        "ss.answer.accept": "*",
        "ss.answer.create": "*",
    },
    [ROUTE_PATTERNS.TASKS_ORGANIZE]: {
        "ss.game.media": "*",
        "ss.task.delete": "challengeIds",
        "ss.task.update": "challengeIds",
        "ss.task.create": "*",
    },
    [ROUTE_PATTERNS.GAMES_PATH]: {
        "ss.task.delete": "*",
        "ss.task.update": "*",
    },
    [ROUTE_PATTERNS.DASHBOARD]: {
        "ss.game.update": "gameIds",
        "ss.game.delete": "gameIds",
        "ss.game.create": "*",
        "ss.game.clone": "*",
    },
    [ROUTE_PATTERNS.ANSWERS_MONITOR]: {
        "ss.answer.accept": "*",
        "ss.answer.feature": "answerIds",
        "ss.answer.like": "answerIds",
        "ss.answer.unlike": "answerIds",
        "ss.answer.reject": "answerIds",
        "ss.answer.update": "answerIds",
        "ss.answer.create": "*",
        "ss.comment.create": "answerIds",
        "ss.comment.delete": "answerIds",
        "ss.comment.like": "answerIds",
        "ss.comment.unlike": "answerIds",
        "ss.game.join": "*",
        "ss.team.create": "*",
        "ss.team.delete": "*",
        "ss.team.merge": "*",
        "ss.team.photo": "*",
        "ss.team.rename": "*",
        "ss.team.unmerge": "*",
        "ss.team.update": "*",
    },
    [ROUTE_PATTERNS.TEAMS_LEADERBOARD]: {
        "ss.answer.accept": "*",
        "ss.answer.feature": "*",
        "ss.answer.unfeature": "*",
        "ss.answer.reject": "*",
        "ss.answer.update": "*",
        "ss.game.score": "gameIds",
        "ss.game.rescore": "gameIds",
    },
    [ROUTE_PATTERNS.GAME_EDIT]: {
        "ss.game.update": "gameIds",
        "ss.zone.create": "*",
        "ss.zone.update": "*",
        "ss.zone.delete": "*",
        "ss.waypoint.create": "*",
        "ss.waypoint.update": "*",
        "ss.waypoint.delete": "*",
    },
    [ROUTE_PATTERNS.GAMES_STREAM]: {
        "ss.answer.accept": "*",
        "ss.answer.feature": "answerIds",
        "ss.answer.unfeature": "answerIds",
        "ss.answer.like": "answerIds",
        "ss.answer.unlike": "answerIds",
        "ss.answer.reject": "answerIds",
        "ss.answer.update": "answerIds",
        "ss.game.join": "*",
        "ss.game.media": "*",
    },
    [ROUTE_PATTERNS.TASKS_STREAM]: {
        "ss.answer.accept": "*",
        "ss.answer.feature": "answerIds",
        "ss.answer.unfeature": "answerIds",
        "ss.answer.like": "answerIds",
        "ss.answer.unlike": "answerIds",
        "ss.answer.reject": "answerIds",
        "ss.answer.update": "answerIds",
    },
    [ROUTE_PATTERNS.TEAMS_STREAM]: {
        "ss.answer.accept": "*",
        "ss.answer.feature": "answerIds",
        "ss.answer.unfeature": "answerIds",
        "ss.answer.like": "answerIds",
        "ss.answer.unlike": "answerIds",
        "ss.answer.reject": "answerIds",
        "ss.answer.update": "answerIds",
    },
    [ROUTE_PATTERNS.GROUPS_ORGANIZE]: {
        "ss.group.create": "*",
        "ss.group.delete": "groupIds",
        "ss.group.update": "groupIds",
    },
    [ROUTE_PATTERNS.GAMES_REWARDS]: {
        "ss.reward.create": "*",
        "ss.reward.delete": "rewardIds",
        "ss.reward.update": "rewardIds",
    },
    [ROUTE_PATTERNS.GAMES_GREY]: {
        "ss.game.update": "gameIds",
    },
    [ROUTE_PATTERNS.ANSWERS_SHOW]: {
        "ss.answer.accept": "answerIds",
        "ss.answer.feature": "answerIds",
        "ss.answer.unfeature": "answerIds",
        "ss.answer.like": "answerIds",
        "ss.answer.unlike": "answerIds",
        "ss.answer.reject": "answerIds",
        "ss.answer.update": "answerIds",
        "ss.comment.create": "answerIds",
        "ss.comment.delete": "answerIds",
        "ss.comment.like": "answerIds",
        "ss.comment.unlike": "answerIds",
    },
    [ROUTE_PATTERNS.GAMES_PERSONALIZATIONS]: {
        "ss.personalization.create": "*",
        "ss.personalization.delete": "personalizationIds",
        "ss.personalization.update": "personalizationIds",
    },
    [ROUTE_PATTERNS.GAMES_NPC]: {
        "ss.npc.create": "*",
        "ss.npc.delete": "npcIds",
        "ss.npc.update": "npcIds",
    },
    [ROUTE_PATTERNS.GAMES_DESIGNATIONS]: {
        "ss.designation.create": "*",
        "ss.designation.delete": "designationIds",
        "ss.designation.update": "designationIds",
    },
};

/**
 * Check if a route has listeners for a specific event source
 * @param {string} route - Route pattern
 * @param {string} source - Event source (e.g., "ss.team.update")
 * @returns {boolean}
 */
export const hasListener = (route, source) => {
    return !!routeListenerMap[route]?.[source];
};

/**
 * Hook for managing page-specific websocket event listeners
 * @param {string} route - Route pattern (e.g., ROUTE_PATTERNS.TEAMS_ORGANIZE)
 * @param {object} data - Page data containing IDs or conditions
 * @returns {object} Listeners config for Refresher component
 * 
 * @example
 * const listeners = usePageListeners(ROUTE_PATTERNS.TEAMS_ORGANIZE, { teamIds });
 */
const EMPTY_LISTENER_DATA = {};

export const usePageListeners = (route, data = EMPTY_LISTENER_DATA) => {
    return useMemo(() => {
        const config = routeListenerMap[route];
        if (!config) return {};
        
        const listeners = {};
        
        for (const [source, resolver] of Object.entries(config)) {
            if (typeof resolver === "function") {
                // Dynamic resolver (e.g., conditional logic), e.g. "ss.answer.feature": (data) => data.featured ? "*" : null
                const result = resolver(data);
                if (result) {
                    listeners[source] = result === "*" ? ["*"] : data[result] || [];
                }
            } else if (resolver === "*") {
                // Wildcard - always listen regardless of the data
                listeners[source] = ["*"];
            } else {
                // ID array resolver, e.g. "ss.team.update": "teamIds" -> data.teamIds must include the affected team
                listeners[source] = data[resolver] || [];
            }
        }
        
        return listeners;
    }, [route, data]);
};